import { formatDate, isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Router } from '@angular/router';
import { customRange } from '@next/arable-lib/shared';
import { CoreActions } from '@next/core-lib';
import { CartActions } from '@next/core-lib/cart';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SessionActions } from '@next/core-lib/session';
import { CustomerStatus, SelectedCustomer, SelectedLocation, UserActions, UserSelectors } from '@next/core-lib/user';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { EMPTY, forkJoin, from, of } from 'rxjs';
import { catchError, concatMap, delay, exhaustMap, filter, finalize, map, mergeMap, startWith, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { Environment } from 'src/environment';

import {
  confirmOrderForOverviewV2,
  createCompleteReport,
  createOrder,
  createOrderForDigitalProducts,
  createOrderReport,
  createOrderSuccess,
  deleteOrderLine,
  deleteOrderLines,
  editCustomerProductInventoryForOverviewV2,
  editNumberOfPeriods,
  editOrderLine,
  editPreferredDeliveryPeriods,
  getCropAids,
  getCropFields,
  getCustomerData,
  getCustomerProductInventory,
  getCustomerPreferences,
  getDefaultYear,
  getDigitalProductsSubscriptions,
  getDigitalProductsSubscriptionsSuccess,
  getDynamicCustomerOrderPlanData,
  getLoggedInEmployeeData,
  getOrderLines,
  getOrderPlan,
  getOrderPlanOverviewListSuccess,
  getOrderPlanOverviewV2List,
  getPackages,
  getProprietaryCropAids,
  getSalesOrderPeriods,
  getShipTosWithOrders,
  getStockFromTelersdashboard,
  handleConfirmOrderForOverview,
  linkCropFields,
  loadCustomerProgress,
  loadCustomerProgressSuccess,
  loadOrderedBemProducts,
  loadOrderedBemProductsSuccess,
  openDigitalProductsDialog,
  openFieldAdvice,
  openFieldPlan,
  openMoveProductData,
  optimizeOrderPlan,
  optimizeOrderPlanSuccess,
  resetYear,
  saveCropFieldLinks,
  saveCustomerProductInventory,
  saveDeliveryPeriodMoments,
  saveDigitalProductsSubscriptions,
  saveDigitalProductsSubscriptionsSuccess,
  saveOrderLine,
  saveOrderLines,
  savePeriodSystem,
  savePreferredDeliveryPeriods,
  selectDeliveryPeriodMoment,
  selectDeliveryPreference,
  selectLocation,
  selectPeriodsToOrder,
  setAvailableYears,
  setCropAids,
  setCropFields,
  setCustomerProductInventory,
  setCustomerPreferences,
  setDefaultYear,
  setLoggedInEmployeeData,
  setOrderLines,
  setPackages,
  setPeriodSystem,
  setPreferredDeliveryPeriods,
  setProprietaryCropAids,
  setSalesOrderPeriods,
  setShipTosWithOrders,
  setYear,
  toggleCustomerProgressOrderplan,
  validationErrorHappened,
} from './actions';
import {
  getAvailableYears,
  selectDefaultYear,
  getYear,
  isAnySprayLicenseActive,
  isPeriodExpiredHelper,
  selectActiveCropAids,
  selectCropAids,
  selectCropFieldLinkType,
  selectCropFields,
  selectCurrentDate,
  selectCustomerProductInventory,
  selectCustomerProductInventoryData,
  selectCustomerProgress,
  selectDigitalProductSubscriptions,
  selectDisplayPeriods,
  selectInactiveCropAidsInOrderplan,
  selectInactivePackagesInOrderplan,
  selectNotOpenGBMOrderLines,
  selectOpenGbmOrderLines,
  selectOrderplanV2Display,
  selectOrderReports,
  selectPackages,
  selectPeriodSystem,
  selectPreferredDeliveryPeriods,
  selectSalesOrderPeriods,
  selectShipTosWithOrders,
  selectBemOrderLines,
} from './selectors';
import { FertilisersApiService } from '../fertilisers-overview/services/api.service';
import { GetCustomerFertilizerStock, GetFertiliserOrderplan, SilentlyOptimizeOrderLine } from '../fertilisers-overview/store/actions/fertilisers-actions';
import { CustomerProductInventoryDialogComponent } from '../overview/components/customer-product-inventory-dialog/customer-product-inventory-dialog.component';
import { LinkCropfieldDialogComponent } from '../overview/components/link-cropfield-dialog/link-cropfield-dialog.component';
import { OrderLineDialogComponent } from '../overview/components/order-line-dialog/order-line-dialog.component';
import {
  CropAid,
  DeliveryPeriodMoment,
  DeliveryPeriodMomentType,
  KeyValue,
  ManuallyAddedCropAidDisplay,
  OrderLine,
  OrderplanCropAidDisplayData,
  OrderplanRequisiteDisplaySummary,
  Package,
  PackageVersion,
  PeriodSystem,
  PreferredDeliveryPeriods,
  SalesOrderPeriod,
  TreatmentTypes,
} from '../overview/models';
import { SwitchTranslationService } from '../overview/services/switch-translation.service';
import { ConfirmOrderDialogOverviewV2Component } from '../overview-v2/components/confirm-order-dialog/confirm-order-dialog.component';
import { DigitalProductsDialogComponent } from '../overview-v2/components/digital-products-dialog/digital-products-dialog.component';
import { MoveProductsDialogComponent } from '../overview-v2/components/move-products-dialog/move-products-dialog.component';
import { ValidationErrorsDialogComponent } from '../overview-v2/components/validation-errors-dialog/validation-errors-dialog.component';
import { DigitalProductSubscription, DeliveryPreference, OrderLineStatus, OrderPlanOverviewV2, PeriodOrderPlanWithDetails } from '../overview-v2/models';
import { ApiService } from '../overview-v2/services/api.service';
import { MultipleSelectDialogComponent } from '../shared/components/multiple-select-dialog/multiple-select-dialog.component';
import { AppState, CropField, CropFieldLinkTypesEnum, Employee } from '../shared/models';
import { ArableCustomerService } from '../shared/services/arable-customer.service';
import { PeriodService } from '../shared/services/arable-period.service';

@Injectable()
export class AppEffects {
  protected store = inject<Store<AppState>>(Store);
  protected actions$ = inject(Actions);
  protected api = inject(ApiService);
  private readonly fertilisersApi = inject(FertilisersApiService);
  private readonly router = inject(Router);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);
  private readonly switchTranslation = inject(SwitchTranslationService);
  private readonly periodService = inject(PeriodService);
  private readonly arableCustomerService = inject(ArableCustomerService);
  private readonly env = inject<Environment>(EnvironmentInjector);
  private readonly platform = inject(PLATFORM_ID);

  /**
   * userInitializedBefore is used to execute actions on the browser side during startup.
   */
  userInitializedBefore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitializedBefore),
      concatMap((_) => [getDefaultYear(), getPackages(), getCropAids()]),
    ),
  );
  /**
   * userInitialized+isPlatformBrowser does the same as above, but also works for newly
   * initialized sessions when users log in on this site.
   */
  userInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.userInitialized),
      filter(() => isPlatformBrowser(this.platform)),
      concatMap((_) => [getDefaultYear(), getPackages(), getCropAids()]),
    ),
  );

  getDefaultYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDefaultYear),
      switchMap(() => this.periodService.getDefaultYear().pipe(map((defaultYear: number) => setDefaultYear({ defaultYear })))),
    ),
  );

  setDefaultYear$ = createEffect(() => {
    const currentYear = new Date().getFullYear();
    const rangeStartYear = currentYear - 3;
    const rangeEndYear = currentYear + 1;
    const availableCropYears = customRange(rangeStartYear, rangeEndYear);
    return this.actions$.pipe(
      ofType(setDefaultYear),
      withLatestFrom(this.store.pipe(select(selectDefaultYear))),
      withLatestFrom(this.store.pipe(select(UserSelectors.selectLocation))),
      switchMap(([_, location]) => [
        setAvailableYears({ availableYears: isNaN(currentYear) ? [] : availableCropYears }),
        getSalesOrderPeriods(),
        ...(location ? [getCustomerData()] : []),
      ]),
    );
  });

  // on session initialized and when the default year is set
  // we fetch data specific to the selected year
  sessionInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SessionActions.sessionInitialized, setDefaultYear),
      map((_) => getSalesOrderPeriods()),
    ),
  );

  // on when the user selects another year
  // we fetch data specific to the selected year
  setYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setYear),
      concatMap((_) => [getSalesOrderPeriods(), getCustomerData()]),
    ),
  );

  customerInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerInitialized),
      map((_) => selectLocation()),
    ),
  );

  customerChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.customerChanged),
      map((_) => selectLocation()),
    ),
  );

  locationInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationInitialized),
      withLatestFrom(this.store.pipe(select(selectDefaultYear))),
      switchMap(([_, defaultYear]) => (defaultYear ? of(getCustomerData()) : EMPTY)),
    ),
  );

  locationChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.locationChanged),
      map(() => getCustomerData()),
    ),
  );

  /**
   * Gets all relevant data; the customer, orderplan, cropfields, etc.
   */
  getCustomerData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCustomerData),
      withLatestFrom(this.store.pipe(select(getYear))),
      concatMap(([_, cropYear]) => {
        if (this.router.url.match(/^\/old.*/)) {
          return [
            getOrderPlan(),
            getOrderLines(),
            getCustomerPreferences(),
            getProprietaryCropAids(),
            getCropFields(),
            getShipTosWithOrders(),
            loadCustomerProgress(),
            getLoggedInEmployeeData(),
            optimizeOrderPlanSuccess({ isOrderPlanOptimized: false }),
          ];
        } else if (this.router.url.match(/^\/fertilizerOrders.*/)) {
          return [
            getProprietaryCropAids(),
            getCropFields(),
            getShipTosWithOrders(),
            getLoggedInEmployeeData(),
            new GetFertiliserOrderplan(),
            new GetCustomerFertilizerStock(),
          ];
        } else {
          return [
            getOrderLines(),
            getCustomerPreferences(),
            getProprietaryCropAids(),
            getCropFields(),
            getShipTosWithOrders(),
            loadCustomerProgress(),
            getLoggedInEmployeeData(),
            optimizeOrderPlanSuccess({ isOrderPlanOptimized: false }),
            getOrderPlanOverviewV2List(),
            getCustomerProductInventory(),
            getDigitalProductsSubscriptions({ openDialog: false }),
            loadOrderedBemProducts({ cropYear }),
          ];
        }
      }),
    ),
  );

  /**
   * Gets all orderplan-specific dynamic data, omits anything that
   * is not relevant for a partial refresh of ordering data.
   */
  getDynamicCustomerOrderPlanData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDynamicCustomerOrderPlanData),
      concatMap((_) => {
        const actions: Action[] = [getOrderLines(), getShipTosWithOrders()];
        if (this.router.url.match(/^\/old.*/)) {
          actions.push(getOrderPlan());
        } else {
          actions.push(getOrderPlanOverviewV2List());
        }
        return actions;
      }),
    ),
  );

  getLoggedInEmployee$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getLoggedInEmployeeData),
      switchMap((_) =>
        this.api.getLoggedInEmployee().pipe(
          map((adviser: Employee) => setLoggedInEmployeeData({ loggedInEmployee: adviser })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          startWith(CoreActions.setLoading({ loading: true })),
        ),
      ),
    ),
  );

  editOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editOrderLine),
      withLatestFrom(this.store.pipe(select(selectActiveCropAids)), this.store.pipe(select(selectPackages))),
      map(([action, cropAids, packages]: [ReturnType<typeof editOrderLine>, CropAid[], Package[]]) => [
        action,
        action.cropAidId ? cropAids.filter(({ id }) => action.cropAidId === id) : cropAids,
        packages,
      ]),
      switchMap(([action, cropAids, packages]: [ReturnType<typeof editOrderLine>, CropAid[], Package[]]) =>
        packages.length > 0
          ? this.dialog
              .form(OrderLineDialogComponent, {
                packages,
                cropAids,
                cropAidId: action.cropAidId,
                orderLine: action.orderLine,
                altButton: !!action.orderLine.packageId,
                altButtonColor: 'warn',
                title: action.orderLine.packageId ? this.switchTranslation.get('editOrderLine') : this.switchTranslation.get('addOrderLine'),
              })
              .pipe(
                switchMap(({ status, result }) => {
                  switch (status) {
                    case DIALOG_RETURN_TYPES.alt:
                      return of(deleteOrderLine({ id: action.orderLine.id }));
                    case DIALOG_RETURN_TYPES.cancel:
                      return EMPTY;
                    case DIALOG_RETURN_TYPES.confirm:
                      return result?.id ? of(saveOrderLine({ orderLine: result, cropAidId: result.cropAidId })) : EMPTY;
                  }
                }),
              )
          : this.dialog.notify({ title: this.locale.translate('noPackages') }).pipe(switchMap((_) => EMPTY)),
      ),
    ),
  );

  editCustomerProductInventoryForOverviewV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editCustomerProductInventoryForOverviewV2),
      withLatestFrom(this.store.pipe(select(selectCustomerProductInventoryData))),
      switchMap(([_, orderplanByCropAids]) =>
        this.dialog
          .form(CustomerProductInventoryDialogComponent, {
            orderplanByCropAids: orderplanByCropAids.sort((a, b) => (a.cropAid.name > b.cropAid.name ? 1 : a.cropAid.name < b.cropAid.name ? -1 : 0)),
            title: this.locale.translate('editCustomerProductInventory'),
            confirmButton: !!(orderplanByCropAids && orderplanByCropAids.length),
            altButton: !!(orderplanByCropAids && orderplanByCropAids.length),
            altButtonText: this.locale.translate('print'),
            altButtonColor: 'primary',
            altButtonIcon: 'print',
            pushHistoryState: false,
          })
          .pipe(
            switchMap(({ status, result }) => {
              switch (status) {
                case DIALOG_RETURN_TYPES.cancel:
                  return EMPTY;
                case DIALOG_RETURN_TYPES.alt:
                  if (isPlatformBrowser(this.platform)) {
                    this.router.navigate(['inventory']);
                  }
                  return EMPTY;
                case DIALOG_RETURN_TYPES.confirm:
                  return of(saveCustomerProductInventory({ productInventories: result }));
              }
            }),
          ),
      ),
    ),
  );

  getStockFromTelersdashboard$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getStockFromTelersdashboard),
      withLatestFrom(this.store.pipe(select(getYear))),
      filter(([_, currentYear]) => !!currentYear),
      exhaustMap(([_, currentYear]) =>
        this.api.importStockFromTelersdashboard(currentYear).pipe(
          concatMap(({ importedCount, year }) => [
            getOrderPlanOverviewV2List(),
            getCustomerProductInventory(),
            NotificationActions.successSnackbar({
              message: this.locale.translate('stockFetchedFromTelersdashboard', {
                count: `${importedCount}`,
                year: `${year}`,
              }),
            }),
          ]),
          catchError(() =>
            of(NotificationActions.errorSnackbar({ message: this.locale.translate('errors.UN001') })),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getCustomerProductInventory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCustomerProductInventory),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (!year) {
          return EMPTY;
        }

        return this.api.getCustomerProductInventory(year).pipe(
          map(({ productInventories }) => setCustomerProductInventory({ productInventories })),
          catchError(() => of(NotificationActions.errorSnackbar({ message: this.locale.translate('errors.UN001') }))),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  optimizeOrderplan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(optimizeOrderPlan),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, currentYear]: [ReturnType<typeof optimizeOrderPlan>, number]) =>
        this.dialog
          .select({
            options: [
              { name: `${this.locale.translate('yes')}`, value: true },
              {
                name: `${this.locale.translate('no')}`,
                value: false,
              },
            ],
            selected: true,
            emptyText: this.locale.translate('optimizeOrderplanTitle'),
            title: this.locale.translate('optimizeOrderplanTitle'),
            text: this.locale.translate('optimizeOrderplanDescription'),
            confirmButtonText: 'Ok',
            confirmButton: true,
          })
          .pipe(
            switchMap(({ status, result }) => {
              switch (status) {
                case DIALOG_RETURN_TYPES.cancel:
                  return EMPTY;
                case DIALOG_RETURN_TYPES.confirm:
                  return this.api.optimizeOrderplan(currentYear, result).pipe(
                    concatMap(() => [optimizeOrderPlanSuccess({ isOrderPlanOptimized: result }), getDynamicCustomerOrderPlanData()]),
                    startWith(CoreActions.setLoading({ loading: true })),
                    finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                  );
              }
            }),
          ),
      ),
    ),
  );

  createOrderReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createOrderReport),
        withLatestFrom(this.store.pipe(select(selectOrderReports))),
        switchMap(([_, orderReports]) => {
          orderReports = orderReports.filter((report) => report.cartId !== null || report.orderedId !== null || report.checkoutId !== null);

          // Reduce the different order reports to a single report per cartId
          // OR a single report per checkoutId/orderedId (checkoutId & orderedId
          // are used for old orders, cartId is the modern way)
          orderReports = orderReports.reduce((accumalator, current) => {
            if (
              !accumalator.some(
                (item) =>
                  (item.cartId && current.cartId && item.cartId === current.cartId) ||
                  (item.checkoutId && current.checkoutId && item.checkoutId === current.checkoutId) ||
                  (item.orderedId && current.orderedId && item.orderedId === current.orderedId),
              )
            ) {
              accumalator.push(current);
            }
            return accumalator;
          }, []);

          return orderReports.length === 0
            ? this.dialog.notify({
                title: this.switchTranslation.get('noOrderReportsTitle'),
                text: this.switchTranslation.get('noOrderReportsDescription'),
              })
            : this.dialog.actions({
                options: orderReports.map((report) => ({
                  name: `${this.switchTranslation.get('orderReport')} ${formatDate(
                    report.orderedDate,
                    this.locale.getLocale() === 'nl' ? 'dd-MM-yyyy' : 'yyyy-MM-dd',
                    this.locale.getLocale(),
                  )}`,
                  value: report.cartId || report.checkoutId || report.orderedId,
                  icon: 'list_alt',
                })),
                pushHistoryState: false,
              });
        }),
        tap(({ status, result }) => {
          if (status === DIALOG_RETURN_TYPES.confirm && (result || result === 0)) {
            this.router.navigate(['report', result]);
          }
        }),
      ),
    { dispatch: false },
  );

  createCompleteReport$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createCompleteReport),
        map(() => this.router.navigate(['complete-report'])),
      ),
    { dispatch: false },
  );

  getCustomerPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCustomerPreferences),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.api.getCustomerPreferences(year).pipe(
            filter((result) => !!result),
            map((result) =>
              setCustomerPreferences({
                preferredDeliveryPeriods: {
                  year: result.year,
                  periods: result.preferedDeliveryPeriods,
                },
                periodSystem: result.periodSystem,
              }),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  editPreferedDeliveryPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editPreferredDeliveryPeriods),
      withLatestFrom(
        this.store.pipe(select(selectSalesOrderPeriods)),
        this.store.pipe(select(selectPreferredDeliveryPeriods)),
        this.store.pipe(select(selectPeriodSystem)),
      ),
      switchMap(([_, salesOrderPeriods, preferedDeliveryPeriods, periodSystem]: [never, SalesOrderPeriod[], PreferredDeliveryPeriods, PeriodSystem]) =>
        new Date().getFullYear() > preferedDeliveryPeriods?.year ||
        !salesOrderPeriods.filter((salesOrderPeriod) => salesOrderPeriod.periodType === periodSystem).length
          ? this.dialog
              .notify({
                title: this.locale.translate('preferedDeliveryPeriods'),
                text: this.locale.translate('noDeliveryPeriods'),
              })
              .pipe(switchMap((__) => EMPTY))
          : this.dialog
              .multiselect({
                options: salesOrderPeriods
                  .filter((salesOrderPeriod) => salesOrderPeriod.periodType === periodSystem)
                  .map((salesOrderPeriod) => ({
                    name: `${salesOrderPeriod.name} (${new Date(salesOrderPeriod.startDispatchFrom).toLocaleDateString(
                      this.locale.getLocale().toString(),
                    )} - ${new Date(salesOrderPeriod.deliverBefore).toLocaleDateString(this.locale.getLocale().toString())})`,
                    value: salesOrderPeriod.id,
                  })),
                selected: preferedDeliveryPeriods.periods,
                title: `${this.locale.translate('preferedDeliveryPeriods')} ${preferedDeliveryPeriods.year}`,
                emptyText: this.locale.translate('noDeliveryPeriods'),
                confirmButtonText: this.locale.translate('save'),
                confirmButton: true,
              })
              .pipe(
                switchMap(({ status, result }) => {
                  switch (status) {
                    case DIALOG_RETURN_TYPES.cancel:
                      return EMPTY;
                    case DIALOG_RETURN_TYPES.confirm:
                      return result.length ? of(savePreferredDeliveryPeriods({ periods: result })) : EMPTY;
                  }
                }),
              ),
      ),
    ),
  );

  editNumberOfPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(editNumberOfPeriods),
      withLatestFrom(this.store.pipe(select(selectPeriodSystem)), this.store.pipe(select(selectNotOpenGBMOrderLines))),
      switchMap(([_, periodSystem, notOpenGBMOrderLines]: [never, PeriodSystem, OrderLine[]]) =>
        notOpenGBMOrderLines.length > 0
          ? this.dialog
              .notify({
                title: this.switchTranslation.get('numberOfPeriodsOrderlinesOrderedTitle'),
                text: this.switchTranslation.get('numberOfPeriodsOrderlinesOrderedText'),
              })
              .pipe(switchMap((__) => EMPTY))
          : this.dialog
              .select({
                options: [
                  { name: '12 (GBM)', value: PeriodSystem.GBM },
                  {
                    name: `24 (${this.locale.translate('horticulture')})`,
                    value: PeriodSystem.Horticulture,
                  },
                ],
                selected: periodSystem,
                emptyText: this.locale.translate('selectNumberOfPeriodsEmpty'),
                title: this.locale.translate('selectNumberOfPeriodsTitle'),
                confirmButtonText: this.locale.translate('save'),
                confirmButton: true,
              })
              .pipe(
                switchMap(({ status, result }) => {
                  switch (status) {
                    case DIALOG_RETURN_TYPES.cancel:
                      return EMPTY;
                    case DIALOG_RETURN_TYPES.confirm:
                      if (result === periodSystem) {
                        return EMPTY;
                      }

                      return of(savePeriodSystem({ periodSystem: result }));
                  }
                }),
              ),
      ),
    ),
  );

  linkCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(linkCropFields),
      withLatestFrom(
        this.store.pipe(select(UserSelectors.selectLocation)),
        this.store.pipe(select(selectCropFields)),
        this.store.pipe(select(UserSelectors.selectLocations)),
        this.store.pipe(select(selectShipTosWithOrders)),
        this.store.pipe(select(selectCropFieldLinkType)),
      ),
      switchMap(
        ([_, location, cropFields, locations, shipTosWithOrders, cropFieldLinkType]: [
          never,
          SelectedLocation,
          CropField[],
          SelectedLocation[],
          number[],
          CropFieldLinkTypesEnum,
        ]) => {
          const cropFieldLinks = cropFields
            .map((c) => ({
              ...c,
              linkedToThisLocation: c.shipToId === location.id,
            }))
            .sort((a, b) => (a.name > b.name ? 1 : a.name < b.name ? -1 : 0));

          let dialogTitle: string;
          switch (cropFieldLinkType) {
            case CropFieldLinkTypesEnum.presales:
              dialogTitle = this.locale.translate('linkPresalesCropFields');
              break;
            case CropFieldLinkTypesEnum.fertilizer:
              dialogTitle = this.locale.translate('linkFertilizerCropFields');
              break;
          }

          return this.dialog
            .form(LinkCropfieldDialogComponent, {
              title: dialogTitle,
              location,
              cropFieldLinks,
              locations,
              shipTosWithOrders,
              confirmButton: true,
              pushHistoryState: false,
              cropFieldLinkType,
            })
            .pipe(
              switchMap(({ status, result }) => {
                switch (status) {
                  case DIALOG_RETURN_TYPES.cancel:
                    return EMPTY;
                  case DIALOG_RETURN_TYPES.confirm:
                    return of(saveCropFieldLinks({ cropFields: result.cropFields }));
                }
              }),
            );
        },
      ),
    ),
  );

  saveCropFieldLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveCropFieldLinks),
      withLatestFrom(this.store.pipe(select(UserSelectors.selectLocation)), this.store.pipe(select(getYear)), this.store.pipe(select(selectCropFieldLinkType))),
      switchMap(([{ cropFields }, location, year, linkCropFieldsType]) => {
        const cropFieldIds = (cropFields as CropField[]).filter((f) => f.shipToId === location.id).map((f) => f.id);

        return this.api.saveLocationCropFields(year, cropFieldIds, linkCropFieldsType).pipe(
          switchMap(() => {
            const baseActions = [getCustomerData(), getDigitalProductsSubscriptions({ openDialog: true })];
            if (linkCropFieldsType !== CropFieldLinkTypesEnum.fertilizer) {
              return from(baseActions);
            }

            return this.fertilisersApi.getFertiliserOrderplan(year).pipe(
              switchMap((orderplan) => {
                const optimizeActions = (orderplan?.orderableProducts ?? [])
                  .map((product) => product.orderLineId)
                  .filter((id): id is number => typeof id === 'number' && id > 0)
                  .map((id) => new SilentlyOptimizeOrderLine(id));

                return from([...baseActions, ...optimizeActions]);
              }),
              catchError(() => from(baseActions)),
            );
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  savePreferedDeliveryPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePreferredDeliveryPeriods),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ periods }, year]) =>
        this.api.upsertPreferedDeliveryPeriods(year, periods).pipe(
          map(() => setPreferredDeliveryPeriods({ preferredDeliveryPeriods: { year, periods } })),
          map(() => getCustomerData()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  savePeriodSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(savePeriodSystem),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ periodSystem }, year]: [ReturnType<typeof savePeriodSystem>, number]) =>
        this.api.upsertPeriodSystem(year, periodSystem).pipe(
          concatMap(() => [setPeriodSystem({ periodSystem: periodSystem === PeriodSystem.GBM ? PeriodSystem.GBM : PeriodSystem.Horticulture })]),
          delay(4000), // Fix bug where GET calls are instantiated before submission is fully handled
          concatMap(() => [getCustomerData(), getSalesOrderPeriods()]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  confirmOrderForOverviewV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmOrderForOverviewV2),
      withLatestFrom(
        this.store.pipe(select(selectOrderplanV2Display)),
        this.store.pipe(select(selectOpenGbmOrderLines)),
        this.store.pipe(select(selectSalesOrderPeriods)),
        this.store.pipe(select(selectInactiveCropAidsInOrderplan)),
        this.store.pipe(select(selectInactivePackagesInOrderplan)),
        this.store.pipe(select(selectCropAids)),
        this.store.pipe(select(selectPackages)),
        this.store.pipe(select(selectPreferredDeliveryPeriods)),
        this.store.pipe(select(selectCurrentDate)),
        this.store.pipe(select(selectDigitalProductSubscriptions)),
      ),
      switchMap(
        ([
          _,
          orderplanV2,
          openOrderLines,
          salesOrderPeriods,
          inactiveCropAids,
          inactivePackages,
          cropAids,
          packages,
          preferedDeliveryPeriods,
          currentDate,
          digitalProductSubscriptions,
        ]: [
          ReturnType<typeof confirmOrderForOverviewV2>,
          OrderPlanOverviewV2[],
          OrderLine[],
          SalesOrderPeriod[],
          CropAid[],
          Package[],
          CropAid[],
          Package[],
          PreferredDeliveryPeriods,
          Date,
          DigitalProductSubscription[],
        ]) => {
          if (orderplanV2.length === 0 && this.filterDigitalProductsToCart(digitalProductSubscriptions)?.length === 0) {
            return this.dialog
              .notify({
                title: this.switchTranslation.get('noItemsInBasketTitle'),
                text: this.switchTranslation.get('noItemsInBasketDescription'),
              })
              .pipe(switchMap((__) => EMPTY));
          }
          const orderPlanByPeriods = this.mapOrderplanV2ForConfirmOrder(orderplanV2, openOrderLines, packages, salesOrderPeriods).sort((a, b) =>
            a.periodId < b.periodId ? -1 : 1,
          );
          const orderPlanWithExpiredPeriod = orderPlanByPeriods.filter((orderplanByPeriod) =>
            isPeriodExpiredHelper(
              salesOrderPeriods.find((x) => x.id === orderplanByPeriod.periodId),
              currentDate,
            ),
          );

          inactiveCropAids.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
          const filteredDigitalProducts = this.filterDigitalProductsToCart(digitalProductSubscriptions);
          return this.dialog
            .fullscreenDialog(ConfirmOrderDialogOverviewV2Component, {
              orderplanByPeriods: orderPlanByPeriods,
              preferedDeliveryPeriods,
              expiredOrderplanByPeriods: orderPlanWithExpiredPeriod,
              inactiveCropAids,
              inactivePackages,
              cropAids,
              title: this.switchTranslation.get('orderOverview'),
              confirmButton: !inactiveCropAids.length && !inactivePackages.length && !orderPlanWithExpiredPeriod.length,
              confirmButtonText: this.switchTranslation.get('confirmOrder'),
              disableConfirmButton: !orderPlanByPeriods?.length && !filteredDigitalProducts?.length,
              digitalProductSubscriptions: filteredDigitalProducts,
            })
            .pipe(
              switchMap(({ status }) => {
                switch (status) {
                  case DIALOG_RETURN_TYPES.cancel:
                    return EMPTY;
                  case DIALOG_RETURN_TYPES.confirm:
                    return of(handleConfirmOrderForOverview());
                }
              }),
            );
        },
      ),
    ),
  );

  handleConfirmOrderForOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(handleConfirmOrderForOverview),
      withLatestFrom(this.store.pipe(select(selectOpenGbmOrderLines)), this.store.pipe(select(selectDigitalProductSubscriptions))),
      concatMap(([_, openOrderLines, digitalProducts]: [ReturnType<typeof handleConfirmOrderForOverview>, OrderLine[], DigitalProductSubscription[]]) => {
        if (openOrderLines.length > 0) {
          return of(selectPeriodsToOrder());
        } else if (digitalProducts.length > 0) {
          return of(createOrderForDigitalProducts());
        }
        return of();
      }),
    ),
  );

  selectPeriodsToOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectPeriodsToOrder),
      withLatestFrom(
        this.store.pipe(select(selectDisplayPeriods)),
        this.store.pipe(select(isAnySprayLicenseActive)),
        this.store.pipe(
          select(selectOpenGbmOrderLines),
        ),
      ),
      concatMap(([_, periods, isAnyLicenseActive]) => {
        return isAnyLicenseActive || this.env.CART_ENABLED
          ? this.dialog
              .select({
                title: this.switchTranslation.get('selectPeriodsForOrder'),
                confirmButtonText: this.locale.translate('next'),
                confirmButton: periods.length > 0,
                emptyText: this.locale.translate('noDeliveryPeriods'),
                options: periods.map((period) => ({
                  name: period.period.name,
                  value: period.period.id,
                })),
                selected: periods[periods.length - 1]?.period.id,
              })
              .pipe(
                switchMap(({ status, result }) => {
                  switch (status) {
                    case DIALOG_RETURN_TYPES.cancel:
                      return EMPTY;
                    case DIALOG_RETURN_TYPES.confirm: {
                      const selectedPeriodNumber = periods.find((period) => period.period.id === result);
                      const filtered = periods.filter(
                        (period) =>
                          !!period.period?.periodNumber &&
                          !!selectedPeriodNumber.period?.periodNumber &&
                          period.period.periodNumber <= selectedPeriodNumber.period.periodNumber,
                      );
                      if (this.env.CART_ENABLED) {
                        return of(selectDeliveryPreference({ periodIds: filtered.map((item) => item.period.id) }));
                      }
                      return of(selectDeliveryPeriodMoment({ deliveryPeriodMoments: filtered }));
                    }
                  }
                }),
              )
          : this.dialog
              .notify({
                title: this.switchTranslation.get('invalidSprayLicenseTitle'),
                text: this.switchTranslation.get('invalidSprayLicenseText'),
              })
              .pipe(switchMap(() => EMPTY));
      }),
    ),
  );

  selectDeliveryPeriodMoment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectDeliveryPeriodMoment),
      concatMap(({ deliveryPeriodMoments }) =>
        this.dialog.form(MultipleSelectDialogComponent, {
          title: this.locale.translate('selectDeliveryPeriodMoment'),
          confirmButtonText: this.locale.translate('select'),
          confirmButton: deliveryPeriodMoments.length > 0,
          values: Object.keys(DeliveryPeriodMomentType).map((deliveryPeriodMoment: keyof typeof DeliveryPeriodMomentType) => ({
            value: DeliveryPeriodMomentType[deliveryPeriodMoment],
            name: this.locale.translate(deliveryPeriodMoment),
          })),
          items: deliveryPeriodMoments.map(({ period, deliveryPeriodMoment }) => ({
            value: deliveryPeriodMoment,
            name: period.name,
            key: period.id.toString(),
          })),
        }),
      ),
      switchMap(({ status, result }) => {
        switch (status) {
          case DIALOG_RETURN_TYPES.cancel:
            return EMPTY;
          case DIALOG_RETURN_TYPES.confirm: {
            const periodIds = result.map((item) => +item.key);
            return of(
              saveDeliveryPeriodMoments({
                deliveryPeriodMoments: result.map(
                  (item: KeyValue): DeliveryPeriodMoment => ({
                    periodId: +item.key,
                    deliveryPeriodMoment: item.value as DeliveryPeriodMomentType,
                  }),
                ),
              }),
              createOrder({ periodIds }),
            );
          }
        }
      }),
    ),
  );

  selectDeliveryPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(selectDeliveryPreference),
      delay(100),
      concatMap(({ periodIds }) =>
        this.dialog
          .select({
            title: this.locale.translate('selectDeliveryPreference'),
            confirmButtonText: this.locale.translate('continue'),
            confirmButton: true,
            emptyText: '',
            options: [
              {
                name: this.locale.translate('noPreference'),
                value: DeliveryPreference.none,
              },
              {
                name: this.locale.translate('earlyDelivery'),
                value: DeliveryPreference.early,
              },
              {
                name: this.locale.translate('middleDelivery'),
                value: DeliveryPreference.middle,
              },
              {
                name: this.locale.translate('lateDelivery'),
                value: DeliveryPreference.late,
              },
            ],
            selected: DeliveryPreference.none,
          })
          .pipe(
            switchMap(({ status, result }) => {
              switch (status) {
                case DIALOG_RETURN_TYPES.cancel:
                  return EMPTY;
                case DIALOG_RETURN_TYPES.confirm: {
                  const deliveryPreference = result === DeliveryPreference.none ? undefined : result;
                  return of(createOrder({ periodIds, deliveryPreference }));
                }
                default:
                  return EMPTY;
              }
            }),
          ),
      ),
    ),
  );

  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createOrder),
      withLatestFrom(
        this.store.pipe(select(getYear)),
        this.store.pipe(select(UserSelectors.selectCustomer)),
        this.store.pipe(select(UserSelectors.selectLocation)),
      ),
      switchMap(
        ([{ periodIds, deliveryPreference }, year, customer, location]: [ReturnType<typeof createOrder>, number, SelectedCustomer, SelectedLocation]) =>
          customer.status === CustomerStatus.Customer && !customer.orderBlock
            ? this.api.createOrder(year, periodIds, deliveryPreference).pipe(
                map((_) => createOrderSuccess({ customerId: customer.id, locationId: location.id })),
                startWith(CoreActions.setLoading({ loading: true })),
                finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
              )
            : this.dialog
                .notify({
                  title: this.switchTranslation.get('customerNotAllowedToOrderTitle'),
                  text: this.switchTranslation.get('customerNotAllowedToOrderText'),
                })
                .pipe(switchMap((_) => EMPTY)),
      ),
    ),
  );

  createOrderForDigitalProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createOrderForDigitalProducts),
      withLatestFrom(
        this.store.pipe(select(getYear)),
        this.store.pipe(select(UserSelectors.selectCustomer)),
        this.store.pipe(select(UserSelectors.selectLocation)),
        this.store.pipe(select(selectSalesOrderPeriods)),
      ),
      switchMap(
        ([_, year, customer, location, periods]: [
          ReturnType<typeof createOrderForDigitalProducts>,
          number,
          SelectedCustomer,
          SelectedLocation,
          SalesOrderPeriod[],
        ]) =>
          customer.status === CustomerStatus.Customer && !customer.orderBlock
            ? this.api
                .createOrder(
                  year,
                  periods.map((x) => x.id),
                )
                .pipe(
                  map((_) => createOrderSuccess({ customerId: customer.id, locationId: location.id })),
                  startWith(CoreActions.setLoading({ loading: true })),
                  finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
                )
            : this.dialog
                .notify({
                  title: this.switchTranslation.get('customerNotAllowedToOrderTitle'),
                  text: this.switchTranslation.get('customerNotAllowedToOrderText'),
                })
                .pipe(switchMap((_) => EMPTY)),
      ),
    ),
  );

  createOrderSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createOrderSuccess),
      switchMap(({ locationId, customerId }) =>
        this.env.ENABLE_SEND_ORDER && this.env.CART_ENABLED
          ? this.dialog
              .confirm({
                title: this.switchTranslation.get('successfullyOrdered'),
                confirmButtonText: this.locale.translate('openCart'),
                cancelButtonText: this.locale.translate('returnToOverview'),
              })
              .pipe(
                switchMap(({ status }) => {
                  switch (status) {
                    case DIALOG_RETURN_TYPES.cancel:
                      return of(getCustomerData());
                    case DIALOG_RETURN_TYPES.confirm: {
                      const returnUrl = window.location.href;
                      return of(CartActions.openCartUrl({ customerId, locationId, returnUrl })).pipe(delay(100));
                    }
                  }
                }),
              )
          : this.dialog
              .notify({
                title: this.switchTranslation.get(this.env.ENABLE_SEND_ORDER ? 'successfullyOrdered' : 'sendOrderIgnored'),
              })
              .pipe(map(() => getCustomerData())),
      ),
    ),
  );

  resetYear$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetYear),
      withLatestFrom(this.store.pipe(select(getYear)), this.store.pipe(select(getAvailableYears)), this.store.pipe(select(selectDefaultYear))),
      switchMap(([{ year }, currentYear, availableYears, defaultYear]) => {
        let newYear: number = year;
        if (isNaN(newYear) || availableYears.indexOf(newYear) === -1) {
          newYear = defaultYear;
        }
        if (newYear !== currentYear) {
          return of(
            SessionActions.setSession({
              data: {
                arable:
                  newYear === defaultYear
                    ? {}
                    : {
                        cropYear: newYear,
                      },
              },
            }),
            setYear(),
          );
        } else {
          return of(setYear());
        }
      }),
    ),
  );

  getOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getOrderLines),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.api.getOrderLines(year, TreatmentTypes.GBM).pipe(
            map((gbmOrderLines) => setOrderLines({ gbmOrderLines })),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  getPackages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getPackages),
      switchMap((_) =>
        this.api.getPackages().pipe(
          map((packages) => {
            const mappedPackages = packages.map((pack) => {
              const isActive = !pack.isActive
                ? false
                : (this.env.USE_PRODUCT_V2_ID && pack.packageVersion === PackageVersion.V2) ||
                  (!this.env.USE_PRODUCT_V2_ID && pack.packageVersion === PackageVersion.V1);

              return {
                ...pack,
                isActive,
              };
            });
            return setPackages({ packages: mappedPackages });
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCropAids),
      switchMap((_) =>
        this.api.getCropAids().pipe(
          map((cropAidResponse) => {
            const cropAids = cropAidResponse.map(
              (cropAid) =>
                ({
                  id: cropAid.id,
                  productId: this.env.USE_PRODUCT_V2_ID ? (cropAid.productV2Id ?? 0) : cropAid.productId,
                  name: cropAid.name,
                  isActive: cropAid.isActive,
                  unitOfMeasurement: cropAid.unitOfMeasurement,
                  codes: cropAid.cropProtection?.admissions?.map((admission) => admission.number) ?? [],
                  source: cropAid.source,
                }) as CropAid,
            );
            return setCropAids({ cropAids });
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getProprietaryCropAids$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getProprietaryCropAids),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.api.getProprietaryCropAids(year).pipe(
            map((cropAidResponse) => {
              const cropAids = cropAidResponse.map(
                (cropAid) =>
                  ({
                    id: cropAid.id,
                    productId: cropAid.productId,
                    name: cropAid.name,
                    isActive: cropAid.isActive,
                    unitOfMeasurement: cropAid.unitOfMeasurement,
                    codes: cropAid.cropProtection?.admissions?.map((admission) => admission.number) ?? [],
                    source: cropAid.source,
                  }) as CropAid,
              );
              return setProprietaryCropAids({ cropAids });
            }),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  getCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCropFields),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.api.getCropFields(year).pipe(
            map((cropFieldsResponse) => setCropFields({ cropFields: cropFieldsResponse.cropFields })),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  setCropFields$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setCropFields),
      switchMap(({ cropFields }) => {
        if (this.router.url.indexOf('fertilizerOrders') !== -1) {
          if (!cropFields.some((cropField) => cropField.fertilizerShipToId)) {
            return of(linkCropFields({ cropFieldLinkType: CropFieldLinkTypesEnum.fertilizer }));
          }
        } else {
          if (!cropFields.some((cropField) => cropField.shipToId)) {
            return of(linkCropFields({ cropFieldLinkType: CropFieldLinkTypesEnum.presales }));
          }
        }

        return EMPTY;
      }),
    ),
  );

  getShipTosWithOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getShipTosWithOrders),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          let linkType: CropFieldLinkTypesEnum;
          if (this.router.url.indexOf('fertilizerOrders') !== -1) {
            linkType = CropFieldLinkTypesEnum.presales;
          } else {
            linkType = CropFieldLinkTypesEnum.fertilizer;
          }
          return this.api.getShipTosWithOrders(year, linkType).pipe(
            map((response) => setShipTosWithOrders({ shipToIds: response })),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  getSalesOrderPeriods$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getSalesOrderPeriods),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.api.getSalesOrderPeriods(year).pipe(
            map((salesOrderPeriods) => setSalesOrderPeriods({ salesOrderPeriods })),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  deleteOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteOrderLine),
      switchMap(({ id, noDataRefreshNeeded }) =>
        this.api.deleteOrderLine(id).pipe(
          switchMap((_) => {
            if (noDataRefreshNeeded) {
              return of(optimizeOrderPlanSuccess({ isOrderPlanOptimized: false }));
            }
            return [getDynamicCustomerOrderPlanData(), optimizeOrderPlanSuccess({ isOrderPlanOptimized: false })];
          }),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteOrderLines),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ ids }, year]) =>
        this.api.deleteOrderLines(ids, year).pipe(
          concatMap(() => [getDynamicCustomerOrderPlanData(), optimizeOrderPlanSuccess({ isOrderPlanOptimized: false })]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveOrderLine$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveOrderLine),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ orderLine, cropAidId: cropAidId }, currentYear]: [ReturnType<typeof saveOrderLine>, number]) =>
        this.api.upsertOrderLine(currentYear, orderLine, cropAidId).pipe(
          concatMap(() => [getDynamicCustomerOrderPlanData(), optimizeOrderPlanSuccess({ isOrderPlanOptimized: false })]),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveOrderLines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveOrderLines),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([{ orderLines, toPeriod }, currentYear]: [ReturnType<typeof saveOrderLines>, number]) =>
        forkJoin(
          orderLines.map((orderLine) =>
            this.api.upsertOrderLine(
              currentYear,
              {
                ...orderLine,
                id: -1,
                cartId: -1,
                orderedId: -1,
                checkoutId: -1,
                status: OrderLineStatus.open,
                deliveryPeriodMoment: DeliveryPeriodMomentType.empty,
                orderedDate: null,
                addedByOptimizer: false,
                periodId: toPeriod.id,
              },
              orderLine.cropAidId,
            ),
          ),
        ).pipe(
          mergeMap((_) => of(deleteOrderLines({ ids: orderLines.map((orderLine) => orderLine.id) }))),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveDeliveryPeriodMoment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveDeliveryPeriodMoments),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([action, year]: [ReturnType<typeof saveDeliveryPeriodMoments>, number]) =>
        this.api.setDeliveryPeriodMoments(year, action.deliveryPeriodMoments).pipe(
          map(() => getCustomerData()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  saveCustomerProductInventory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveCustomerProductInventory),
      withLatestFrom(this.store.pipe(select(getYear)), this.store.pipe(select(selectCustomerProductInventory))),
      switchMap(([{ productInventories }, currentYear, currentProductInventories]) => {
        // Saving replaces the entire inventory, including products that have no order-plan row.
        const inventoryByCropAidId = new Map(
          [...currentProductInventories, ...productInventories].map((inventory) => [inventory.cropAidId, inventory]),
        );

        return this.api.upsertCustomerProductInventory(currentYear, [...inventoryByCropAidId.values()]).pipe(
          map(() => getCustomerData()),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        );
      }),
    ),
  );

  openFieldPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openFieldPlan),
      map((_) =>
        SessionActions.windowOpenWithSession({
          url: this.env.FIELD_PLAN_URL,
          name: '_self',
        }),
      ),
    ),
  );

  openFieldAdvice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openFieldAdvice),
      map((_) =>
        SessionActions.windowOpenWithSession({
          url: this.env.FIELD_ADVICE_URL,
          name: '_self',
        }),
      ),
    ),
  );

  loadCustomerProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadCustomerProgress),
      withLatestFrom(this.store.pipe(select(getYear))),
      mergeMap(([_, cropYear]) =>
        this.arableCustomerService.getCustomerProgress(cropYear).pipe(
          map((customerProgress) => loadCustomerProgressSuccess({ customerProgress })),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  getOrderPlanOverviewV2List$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getOrderPlanOverviewV2List),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.api.getOrderPlanOverviewList(year).pipe(
            map((orderLines) => getOrderPlanOverviewListSuccess({ orderPlanOverviewV2List: orderLines.result })),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  toggleCustomerProgressOrderplan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(toggleCustomerProgressOrderplan),
      withLatestFrom(this.store.pipe(select(selectCustomerProgress))),
      mergeMap(([_, customerProgress]) =>
        this.arableCustomerService
          .upsertCustomerProgress({
            ...customerProgress,
            processes: {
              ...customerProgress.processes,
              orderplanningOrderCompleted: !customerProgress.processes.orderplanningOrderCompleted,
            },
          })
          .pipe(
            map(() => loadCustomerProgress()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  openMoveProductData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openMoveProductData),
      withLatestFrom(
        this.store.pipe(select(selectSalesOrderPeriods)),
        this.store.pipe(select(selectPeriodSystem)),
        this.store.pipe(select(selectOpenGbmOrderLines)),
        this.store.pipe(select(selectCropAids)),
        this.store.pipe(select(selectPackages)),
      ),
      switchMap(
        ([{ fromPeriodId }, salesOrderPeriods, periodSystem, orderLines, cropAids, packages]: [
          { fromPeriodId: number },
          SalesOrderPeriod[],
          PeriodSystem,
          OrderLine[],
          CropAid[],
          Package[],
        ]) => {
          const deliveryPeriods = salesOrderPeriods.filter((salesOrderPeriod) => salesOrderPeriod.periodType === periodSystem);
          if (!deliveryPeriods.length) {
            return EMPTY;
          }

          return this.dialog
            .form(MoveProductsDialogComponent, {
              fromPeriodId,
              salesOrderPeriods: deliveryPeriods,
              orderLines,
              cropAids,
              packages,
              confirmButton: true,
              title: this.locale.translate('moveProductsTitle'),
              confirmButtonText: this.locale.translate('moveProducts'),
              cancelButtonText: this.locale.translate('cancel'),
              pushHistoryState: false,
              fullscreen: false,
            })
            .pipe(
              switchMap(({ status, result }) => {
                switch (status) {
                  case DIALOG_RETURN_TYPES.cancel:
                    return EMPTY;
                  case DIALOG_RETURN_TYPES.confirm:
                    return of(saveOrderLines({ orderLines: result.selectedOrderLines, toPeriod: result.toPeriod }));
                }
              }),
            );
        },
      ),
    ),
  );

  getDigitalProductsSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDigitalProductsSubscriptions),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([action, year]: [ReturnType<typeof getDigitalProductsSubscriptions>, number]) => {
        if (year) {
          return this.api.getDigitalProductsSubscriptions(year).pipe(
            concatMap((digitalProductSubscription) => {
              const actions: Action[] = [getDigitalProductsSubscriptionsSuccess({ requisites: digitalProductSubscription.requisites })];
              if (digitalProductSubscription?.requisites.length > 0 && action.openDialog) {
                actions.push(openDigitalProductsDialog());
              }
              return actions;
            }),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  openDigitalProductRequisitesDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(openDigitalProductsDialog),
      withLatestFrom(this.store.pipe(select(selectDigitalProductSubscriptions)), this.store.select(selectBemOrderLines)),
      switchMap(([_, digitalProductSubscriptions, bemProducts]) =>
        forkJoin({
          downyMildewActivated: this.api.getModuleActivation('downy-mildew').pipe(catchError(() => of(false))),
          virusMonitorActivated: this.api.getModuleActivation('virus-monitor').pipe(catchError(() => of(false))),
        }).pipe(
          switchMap(({ downyMildewActivated, virusMonitorActivated }) =>
            this.dialog
              .form(DigitalProductsDialogComponent, {
                digitalProductSubscriptions,
                bemProducts,
                downyMildewActivated,
                virusMonitorActivated,
                title: this.locale.translate('digitalProducts'),
                confirmButton: digitalProductSubscriptions?.length > 0,
                pushHistoryState: false,
              })
              .pipe(
                switchMap(({ status, result }) => {
                  switch (status) {
                    case DIALOG_RETURN_TYPES.cancel:
                      return EMPTY;
                    case DIALOG_RETURN_TYPES.confirm:
                      return of(saveDigitalProductsSubscriptions({ requisites: result.digitalProductSubscriptions }));
                  }
                }),
              ),
          ),
        ),
      ),
    ),
  );

  saveDigitalProductsSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveDigitalProductsSubscriptions),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([action, year]: [ReturnType<typeof saveDigitalProductsSubscriptions>, number]) => {
        if (year) {
          return this.api.saveDigitalProductsSubscriptions(action.requisites, year).pipe(
            map((_) => saveDigitalProductsSubscriptionsSuccess()),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  getBemDigitalProducts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadOrderedBemProducts),
      withLatestFrom(this.store.pipe(select(getYear))),
      switchMap(([_, year]) => {
        if (year) {
          return this.api.getBemDigitalProductsSubscriptions(year).pipe(
            map((products) => loadOrderedBemProductsSuccess({ products: products })),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          );
        }
        return EMPTY;
      }),
    ),
  );

  validationErrorHappened$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(validationErrorHappened),
        tap(({ errors }) => {
          this.dialog.responsiveDialog(ValidationErrorsDialogComponent, {
            validationErrors: errors,
            title: this.switchTranslation.get('customerNotAllowedToOrderTitle'),
          });
        }),
      ),
    { dispatch: false },
  );

  sortCropAids = (
    a: OrderplanRequisiteDisplaySummary | ManuallyAddedCropAidDisplay | OrderplanCropAidDisplayData,
    b: OrderplanRequisiteDisplaySummary | ManuallyAddedCropAidDisplay | OrderplanCropAidDisplayData,
  ) => (a.cropAid.name.toLowerCase() > b.cropAid.name.toLowerCase() ? 1 : -1);

  mapOrderplanV2ForConfirmOrder(
    orderplanV2: OrderPlanOverviewV2[],
    openOrderLines: OrderLine[],
    packages: Package[],
    salesOrderPeriods: SalesOrderPeriod[],
  ): PeriodOrderPlanWithDetails[] {
    const openOrderLineIds = openOrderLines.map((orderLine) => orderLine.id).join(',');
    const orderPlanWithOpenOrderLines = orderplanV2.filter((orderplan) => orderplan.orderLineIds.some((id) => openOrderLineIds.includes(id.toString())));

    const orderPlanByPeriods: PeriodOrderPlanWithDetails[] = [];

    orderPlanWithOpenOrderLines.forEach((orderplan) => {
      orderplan.orderplanPeriods
        .filter((period) => period.orderLinesTotalAmount > 0)
        .forEach((orderPlanPeriod) => {
          const foundOpenOrderLine = openOrderLines.find(
            (orderLine) =>
              orderLine.periodId === orderPlanPeriod.periodId && orderLine.cropAidId === orderplan.cropAidId && orderLine.packageId === orderplan.packageId,
          );
          if (!foundOpenOrderLine) {
            return;
          }

          const salesOrderPeriod = salesOrderPeriods.find((sop) => sop.id === orderPlanPeriod.periodId);

          const newOrderPlanByPeriod = {
            periodId: salesOrderPeriod.id,
            periodName: salesOrderPeriod.name,
            startDispatchFrom: salesOrderPeriod.startDispatchFrom,
            deliverBefore: salesOrderPeriod.deliverBefore,
            cropAidId: orderplan.cropAidId,
            cropAidName: orderplan.cropAidName,
            packageId: orderplan.packageId,
            packageCount: foundOpenOrderLine.count,
            packageAmount: packages.find((pkg) => pkg.id === foundOpenOrderLine.packageId).amount,
            packageUnitOfMeasurement: orderplan.packageUnitOfMeasurement ?? orderplan.unitOfMeasurement,
            requisiteAmount: orderPlanPeriod.requisiteAmount,
          } as PeriodOrderPlanWithDetails;

          orderPlanByPeriods.push(newOrderPlanByPeriod);
        });
    });
    return orderPlanByPeriods.sort((a, b) =>
      a.cropAidName.toLowerCase() > b.cropAidName.toLowerCase()
        ? 1
        : a.cropAidName.toLowerCase() < b.cropAidName.toLowerCase()
          ? -1
          : a.packageAmount < b.packageAmount
            ? -1
            : 1,
    );
  }

  private filterDigitalProductsToCart(digitalProducts: DigitalProductSubscription[] | undefined): DigitalProductSubscription[] | undefined {
    return digitalProducts?.filter((x) => x.includeInOrder && x.totalRequisite - x.alreadyOrdered - x.correction > 0);
  }
}
